import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { MarkdownEditor } from '../../components/MarkdownEditor';
import { SymptomQaPreview } from '../../components/SymptomQaPreview';
import type { FaqCategory, ChatMessagesResponse } from '../../types';

export const SymptomFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { categoryId: paramCategoryId, symptomName: paramSymptomName } = useParams<{
    categoryId: string;
    symptomName: string;
  }>();
  const isEdit = Boolean(paramCategoryId && paramSymptomName);
  const decodedSymptomName = paramSymptomName ? decodeURIComponent(paramSymptomName) : '';

  // Form state
  const [categoryId, setCategoryId] = useState(paramCategoryId ?? '');
  const [symptomName, setSymptomName] = useState('');
  const [cause, setCause] = useState('');
  const [solution, setSolution] = useState('');

  // UI state
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // UI labels for preview
  const [causeLabel, setCauseLabel] = useState('原因:');
  const [solutionLabel, setSolutionLabel] = useState('処置方法:');

  // Track original symptom name for update
  const [originalSymptomName, setOriginalSymptomName] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');

  // Load categories
  useEffect(() => {
    apiService
      .getCategories()
      .then((cats) => {
        const parents = cats.filter((c) => !c.productCategoryId);
        setCategories(parents);
        if (!isEdit && parents.length > 0 && !categoryId) {
          setCategoryId(parents[0].id);
        }
      })
      .catch(() => setError('カテゴリの取得に失敗しました'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  // Load UI labels for preview
  useEffect(() => {
    apiService
      .getChatMessages()
      .then((res: ChatMessagesResponse) => {
        if (res.uiLabels.qaDisplayCause) setCauseLabel(res.uiLabels.qaDisplayCause);
        if (res.uiLabels.qaDisplayTreatment) setSolutionLabel(res.uiLabels.qaDisplayTreatment);
      })
      .catch(() => {
        // Use defaults
      });
  }, []);

  // Load existing symptom for edit
  useEffect(() => {
    if (!isEdit || !paramCategoryId || !decodedSymptomName) return;
    setIsLoading(true);
    apiService
      .getSymptomDetail(paramCategoryId, decodedSymptomName)
      .then((detail) => {
        setSymptomName(detail.symptomName);
        setOriginalSymptomName(detail.symptomName);
        setCause(detail.cause);
        setSolution(detail.solution);
        setCategoryId(detail.categoryId);
        setUpdatedAt(detail.updatedAt);
        setUpdatedBy(detail.updatedBy);
      })
      .catch(() => {
        setError('症状データの取得に失敗しました');
        navigate('/symptoms', { replace: true });
      })
      .finally(() => setIsLoading(false));
  }, [isEdit, paramCategoryId, decodedSymptomName, navigate]);

  // beforeunload guard
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const markDirty = useCallback(
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
      (value: T) => {
        setter(value);
        setIsDirty(true);
      },
    [],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!symptomName.trim()) {
      setError('症状名を入力してください');
      return;
    }
    if (!solution.trim()) {
      setError('対処法を入力してください');
      return;
    }
    if (!categoryId) {
      setError('カテゴリを選択してください');
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit && paramCategoryId) {
        await apiService.updateSymptom(paramCategoryId, originalSymptomName, {
          symptomName: symptomName.trim(),
          cause: cause.trim(),
          solution: solution.trim(),
        });
      } else {
        await apiService.createSymptom({
          categoryId,
          symptomName: symptomName.trim(),
          cause: cause.trim(),
          solution: solution.trim(),
        });
      }
      setIsDirty(false);
      navigate('/symptoms');
    } catch (err) {
      const message = err instanceof Error ? err.message : '保存に失敗しました';
      setError(message.includes('409') ? 'この症状名は既に存在します' : '保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="py-12 text-center text-slate-400">読み込み中...</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/symptoms')}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            &larr; 症状一覧
          </button>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? '症状の編集' : '新規症状の追加'}
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate('/symptoms')}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}

      <div className="space-y-6 rounded-lg border border-slate-200 bg-white p-6">
        {/* Category */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">カテゴリ</label>
          {isEdit ? (
            <span className="inline-block rounded-lg bg-blue-50 px-3 py-1.5 text-sm text-blue-700">
              {categories.find((c) => c.id === categoryId)?.name ?? categoryId}
            </span>
          ) : (
            <select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setIsDirty(true);
              }}
              className="w-52 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Symptom Name */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            症状名 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={symptomName}
            onChange={(e) => markDirty(setSymptomName)(e.target.value)}
            placeholder="例: バーナーキャップの汚れで点火しない"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            required
          />
          {isEdit && (
            <p className="mt-1 text-xs text-slate-400">
              症状名を変更すると、セマンティック検索用のベクトルが自動的に再生成されます
            </p>
          )}
        </div>

        {/* Cause (Markdown) */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            原因 <span className="text-slate-400">（任意）</span>
          </label>
          <div className="flex gap-4">
            <div className="flex-1">
              <MarkdownEditor
                value={cause}
                onChange={markDirty(setCause)}
                rows={4}
                placeholder="原因を入力してください"
              />
            </div>
            <div className="flex-1">
              <SymptomQaPreview label={causeLabel} content={cause} />
            </div>
          </div>
        </div>

        {/* Solution (Markdown) */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            対処法 <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <div className="flex-1">
              <MarkdownEditor
                value={solution}
                onChange={markDirty(setSolution)}
                rows={6}
                placeholder="対処法を入力してください"
              />
            </div>
            <div className="flex-1">
              <SymptomQaPreview label={solutionLabel} content={solution} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between">
        {updatedAt && (
          <span className="text-xs text-slate-400">
            最終更新: {new Date(updatedAt).toLocaleString('ja-JP')} by {updatedBy}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={() => navigate('/symptoms')}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </form>
  );
};
